import { DatePipe } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { DataService, GeneralService, ServicecallService } from 'src/app';
import { EntitiesServiceService } from 'src/app/shared/services';

@Component({
  selector: 'app-common-scatter-plot',
  templateUrl: './common-scatter-plot.component.html',
  styleUrl: './common-scatter-plot.component.css'
})
export class CommonScatterPlotComponent implements OnInit, OnChanges, OnDestroy {
  @Input() entityDetails: any;
  @Input() widgetData: any;
  @Input() entityId: string;
  @Input() fromDate: string;
  @Input() toDate : string;

  isloader: boolean = true;
  isError: boolean = false;
  public scatterChartData: any = {};
  private _scatterPlotSubscription: Subscription;
  private _subscription: Subscription;
  private apiHitTime: any;
  public chartData: [number, number][] = [];
  //1Hr time range
  private _startTime: Date =  new Date(new Date().getTime() - 60 * 60 * 1000);
  private chartThreshold: any;
  private _intervalTimer: any;


  constructor(
    private _sharedService: ServicecallService,
    private _datePipe: DatePipe,
    private _dataSer: DataService
  ) {
    this._subscription = this._dataSer?.events?.subscribe((res: any) => {
      if (res.name === 'apiRefresh') {
        this._getAttributeDetails();
      }
    });
   }

  ngOnInit(): void {
    // this._getAttributeDetails();
  }

  ngOnDestroy(): any {
    this._subscription?.unsubscribe();
    this._scatterPlotSubscription?.unsubscribe();
    this._clearTimer();
  }

  ngOnChanges(): void {
    this.chartThreshold = this._sharedService?.getConfigurationBasedOnCategory(this.entityDetails) ? this._sharedService?.getConfigurationBasedOnCategory(this.entityDetails)?.chartThreshold : null;
    this._getAttributeDetails();
  }

  /**
   * Gets request data
   * @returns  
   */
  private _getRequestData() {
    return {
      entityId: this.entityDetails?._id,
      from: this._datePipe.transform(this.fromDate ?? this._startTime, 'yyyy-MM-ddTHH:mm:ssZZ'),
      to: this._datePipe.transform(this.toDate ?? Date.now(), 'yyyy-MM-ddTHH:mm:ssZZ'),
      siteId: this._sharedService.getSiteId(),
      attributes: this.widgetData?.widgetDataAttributes?.map(item => item.attributeName),
      includeHistory: false
    };
  }

  /**
   * Gets attribute details
   */
  private _getAttributeDetails() {
    this.apiHitTime = new Date();
    this._scatterPlotSubscription = this._sharedService.getEntityAttribute(this._getRequestData()).subscribe({
      next: (resp: any) => {
        if (resp?.status) {
          if (resp?.response?.data) {
            const attrData = resp?.response?.data?.attributes;
            const attrX = this.widgetData?.widgetDataAttributes?.[0]?.attributeName;
            const attrY = this.widgetData?.widgetDataAttributes?.[1]?.attributeName;

            this.chartData = attrData ? this.mapMatchingAttributes(attrData, attrX, attrY) : [];
            if(!this.chartData?.length) {
              this.chartData = [[0, 0]];
            }
            this.generateChart(this.chartData);
          } else {
            this.chartData = [[0, 0]];
            this.generateChart(this.chartData)
          }
        }
        this.isloader = false;
      },
      error: () => {
        this.isloader = false;
        this.isError = true;
        this.chartData = [];
      }
    })
  }

  /**
   * Maps matching attributes
   * @param attributes 
   * @param attributeKey1 
   * @param attributeKey2 
   * @returns matching attributes 
   */
  private mapMatchingAttributes(
    attributes: any,
    attributeKey1: string,
    attributeKey2: string
  ): [number | null, number | null][] {
    const array1 = attributes[attributeKey1];
    const array2 = attributes[attributeKey2];
    const result: [number | null, number | null][] = [];

    if (array1 && array2) {
      const attributeMap = new Map(
        array2?.map((item: any) => [item.timestamp, item.value])
      );

      array1?.forEach((item1: any) => {
        const value2: number = Number(attributeMap?.get(item1.timestamp));
        if (value2 !== undefined) {
          result?.push([item1.value, value2]);
        }
      });
    }

    if (array1) {
      array1?.forEach((item1: any) => result?.push([item1.value, 0]));
      return result;
    }
  
    if (array2) {
      array2?.forEach((item2: any) => result?.push([0, item2.value]));
      return result;
    }

    return result;
  }

  /**
   * Generates chart
   * @param chartData 
   */
   generateChart(chartData: [number, number][]) {
    const thresholdColors = this.chartThreshold?.[this.widgetData?.name]?.thresholdColor;
    const thresholdValues = this.chartThreshold?.[this.widgetData?.name]?.thresholdValues;
  
    const yAxisMin = chartData.length > 0
      ? Math.min(...chartData.map((point) => point[1])) - 10
      : -10; // Default minimum with padding
    const yAxisMax = chartData.length > 0
      ? Math.max(...chartData.map((point) => point[1])) + 10
      : 10; // Default maximum with padding
  
    this.scatterChartData = {
      xAxis: {
        type: 'value',
        show: true,
        axisTick: {
          show: false
        },
        name: this.widgetData?.widgetDataAttributes?.[0]?.name ? `${this.widgetData.widgetDataAttributes[0].name}${this.widgetData.widgetDataAttributes[0].uom ? ` (${this.widgetData.widgetDataAttributes[0].uom})` : ''}` : '',
        nameLocation: 'middle',
        nameGap: 23,
        nameTextStyle: {
          fontSize: 10,
          fontFamily: 'Arial',
          fontWeight: 'bold'
        },
      },
      yAxis: {
        type: 'value',
        axisTick: {
          show: false
        },
        axisLine: {
          show: false
        },
        name: this.widgetData?.widgetDataAttributes?.[1]?.name ? `${this.widgetData.widgetDataAttributes[1].name}${this.widgetData.widgetDataAttributes[1].uom ? ` (${this.widgetData.widgetDataAttributes[1].uom})` : ''}` : '',
        nameLocation: 'middle',
        nameGap: 25,
        nameTextStyle: {
          fontSize: 10,
          fontFamily: 'Arial',
          fontWeight: 'bold'
        },
        axisLabel: {
          formatter: (value: number) => {
            return Math.floor(value)?.toString(); // Regular values
          },
          textStyle: {
            fontSize: 12,
            fontFamily: 'Arial'
          }
        },
        min: yAxisMin < 0 ? 0 : yAxisMin, // Ensure min does not go below 0
        max: yAxisMax
      },
      series: [
        {
          symbolSize: 10,
          data: chartData?.map((point: number[]) => ({
            value: point,
            itemStyle: {
              color: point[1] > thresholdValues?.upperThreshold
                ? thresholdColors?.upper
                : point[1] < thresholdValues?.lowerThreshold
                  ? thresholdColors?.lower
                  : thresholdColors?.between
            }
          })),
          itemStyle: {
            color: '#32A7D0'
          },
          type: 'scatter',
          markLine: {
            data: [
              ...(thresholdValues?.upperThreshold !== undefined
                ? [{ yAxis: thresholdValues?.upperThreshold }]
                : []),
              ...(thresholdValues?.lowerThreshold !== undefined
                ? [{ yAxis: thresholdValues?.lowerThreshold }]
                : [])
            ],
            lineStyle: {
              type: 'solid',
              color: 'rgba(250, 174, 165, 1)',
              width: 1
            },
            symbol: 'none',
            label: {
              show: false
            }
          }
        }
      ],
      toolbox: {
        show: false
      },
      grid: {
        top: '10%',
        left: '15%',
        right: '5%',
        bottom: '16%'
      },
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const [x, y] = params.value;
          return `${this.widgetData?.widgetDataAttributes?.[0]?.name?? 'X'}: ${Number(x)?.toFixed(2)}<br/>${this.widgetData?.widgetDataAttributes?.[1]?.name ?? 'Y'}: ${Number(y)?.toFixed(2)}`;
        },
        textStyle: {
          fontSize: 12,
          fontFamily: 'Arial'
        }
      }
    };
  
    this._refreshApi();
  }

   /**
   * Refreshs api
   */
    private _refreshApi() {
      if (+this.widgetData?.refreshRate) {
        this._clearTimer();
        this._intervalTimer = setInterval(() => {
          this._getAttributeDetails();
        }, +this.widgetData?.refreshRate);
        this.isError = false;
      }
    }
  
    /**
     * Clears timer
     */
    private _clearTimer() {
      if (this._intervalTimer) {
        clearInterval(this._intervalTimer);
        this._intervalTimer = null;
      }
    }

}
